import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import { isSameMinute } from 'date-fns';
import PropTypes from 'prop-types';
import styled from 'styled-components';

// Components
import CalendarPlusIcon from '@icons/CalendarPlusIcon';
import { BodySmall, ChipButton, H5, LabelSmall, LinkText } from '@styled';
// import ToggleSwitch from '@atoms/ToggleSwitch';

// Helpers
import colors from '@general/colors';
import { formatDateInTimeZone } from '@general/dates';
import { availabilityStatuses } from '@general/helpers/scheduling.helpers';

const TimeSlotOuterWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    margin-bottom: -16px;
    margin-left: -8px;
    margin-right: -8px;
`;

const TimeSlotInnerWrapper = styled.div`
    display: flex;
    flex-direction: column;
`;

const TimeSlotText = styled(LabelSmall)`
    color: white;
    margin-left: 8px;

    &.unavailable {
        color: ${colors.background};
    }
`;

// const AvailabilityToggleContainer = styled.div`
//     align-items: center;
//     display: flex;
//     margin-top: 37px;
// `;

const TimeSlotsPanel = ({ columns = 3, header, subheader, selectedTimeSlot, timeSlots, timeZone, onTimeSlotClick }) => {
    // const [isDayOpen, setIsDayOpen] = useState(true);
    const [slotsPerColumn, setSlotsPerColumn] = useState(0);
    const [extraSlots, setExtraSlots] = useState(false);

    useEffect(() => {
        setSlotsPerColumn(timeSlots.length >= columns ? Math.floor(timeSlots.length / columns) : 1);
        setExtraSlots(timeSlots.length >= columns ? timeSlots.length % columns : 0);
    }, [timeSlots, columns]);

    return (
        <div className="time-slots-panel">
            <H5 className="mb-0.75rem">{header}</H5>
            <BodySmall className="mb-1.5rem">{subheader}</BodySmall>

            <TimeSlotOuterWrapper>
                {timeSlots.length > 0 &&
                    slotsPerColumn > 0 &&
                    [...Array(columns).keys()].map(columnIndex => {
                        let offset = 0;

                        if (columnIndex < extraSlots) {
                            offset = columnIndex;
                        } else if (columnIndex >= extraSlots) {
                            offset = extraSlots;
                        }

                        const startIndex = columnIndex * slotsPerColumn + offset;
                        const includeExtraSlot = extraSlots > columnIndex;
                        const slotsForColumn = timeSlots.slice(
                            startIndex,
                            startIndex + slotsPerColumn + (includeExtraSlot ? 1 : 0)
                        );

                        return (
                            <TimeSlotInnerWrapper key={columnIndex}>
                                {slotsForColumn.map(timeSlot => {
                                    const isUnavailable = timeSlot.availability.status === 'unavailable';

                                    return (
                                        <ChipButton
                                            key={timeSlot.start}
                                            type="button"
                                            className={classnames({
                                                active:
                                                    selectedTimeSlot &&
                                                    isSameMinute(timeSlot.start, selectedTimeSlot.start),
                                                disabled: isUnavailable,
                                            })}
                                            onClick={() => onTimeSlotClick(timeSlot)}
                                        >
                                            <CalendarPlusIcon
                                                color={isUnavailable ? colors.background : 'white'}
                                                size="small"
                                            />
                                            <TimeSlotText className={classnames({ unavailable: isUnavailable })}>
                                                {formatDateInTimeZone(timeSlot.start, 'h:mm aa', timeZone.tzCode)}
                                            </TimeSlotText>
                                        </ChipButton>
                                    );
                                })}
                            </TimeSlotInnerWrapper>
                        );
                    })}
            </TimeSlotOuterWrapper>

            {/*
            <AvailabilityToggleContainer>
                <ToggleSwitch
                    id={formatDateInEastern(timeSlots[0].start, 'yyyy-MM-dd HH:mm')}
                    checked={isDayOpen}
                    onChange={() => setIsDayOpen(!isDayOpen)}
                />
                <LinkText className="ml-0.5rem">Availability for the day set to open</LinkText>
            </AvailabilityToggleContainer>
            */}
        </div>
    );
};

const timeSlotShape = {
    start: PropTypes.instanceOf(Date).isRequired,
    end: PropTypes.instanceOf(Date).isRequired,
    availability: PropTypes.oneOf(Object.keys(availabilityStatuses).map(status => availabilityStatuses[status])),
};

TimeSlotsPanel.propTypes = {
    columns: PropTypes.number,
    header: PropTypes.string.isRequired,
    subheader: PropTypes.string.isRequired,
    selectedTimeSlot: PropTypes.shape(timeSlotShape),
    timeSlots: PropTypes.arrayOf(PropTypes.shape(timeSlotShape)),
    timeZone: PropTypes.shape({
        label: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        tzCode: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
        utc: PropTypes.string.isRequired,
    }),
    onTimeSlotClick: PropTypes.func.isRequired,
};

export { availabilityStatuses };
export default TimeSlotsPanel;
